import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SearchX, RotateCcw } from "lucide-react";
import { Container, AxorButton, EASE } from "@/components/primitives";
import { PageHero } from "@/components/PageHero";
import { PrismBackground, PrismHero } from "@/components/Prism";
import { JobCard } from "@/components/JobCard";
import { JobFilters } from "@/components/JobFilters";
import { JobDetail } from "@/components/JobDetail";
import { CTASection } from "@/components/CTASection";
import { jobs } from "@/data/jobs";

const defaultFilters = {
  location: "All Locations",
  industry: "All Industries",
  experience: "All Levels",
  type: "All Types",
};

export default function Jobs() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedJob, setSelectedJob] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter((j) => {
      const matchesSearch =
        !q ||
        j.title.toLowerCase().includes(q) ||
        j.industry.toLowerCase().includes(q) ||
        j.skills.some((s) => s.toLowerCase().includes(q));
      const matchesLocation =
        filters.location === "All Locations" || j.location === filters.location;
      const matchesIndustry =
        filters.industry === "All Industries" || j.industry === filters.industry;
      const matchesExp =
        filters.experience === "All Levels" || j.experience === filters.experience;
      const matchesType =
        filters.type === "All Types" || j.type === filters.type;
      return matchesSearch && matchesLocation && matchesIndustry && matchesExp && matchesType;
    });
  }, [search, filters]);

  const reset = () => {
    setSearch("");
    setFilters(defaultFilters);
  };

  return (
    <>
      <PageHero
        overline="Careers"
        title="Find Your Next Opportunity."
        highlight="Next Opportunity"
        description="Explore opportunities that match your skills, experience and ambitions."
        testid="jobs-hero"
      />

      <section className="relative overflow-hidden py-16 md:py-24" data-testid="jobs-listings">
        <PrismBackground />
        <Container className="relative grid gap-10 lg:grid-cols-[320px_1fr]">
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <JobFilters
              filters={filters}
              setFilters={setFilters}
              search={search}
              setSearch={setSearch}
            />
          </aside>

          <div>
            <div className="mb-6 flex items-center justify-between">
              <p className="text-sm text-axor-text" data-testid="jobs-count">
                <span className="font-semibold text-axor-white">{filtered.length}</span>{" "}
                {filtered.length === 1 ? "opportunity" : "opportunities"} found
              </p>
              {(search || JSON.stringify(filters) !== JSON.stringify(defaultFilters)) && (
                <button
                  onClick={reset}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-axor-cyan transition-colors hover:text-axor-turquoise"
                  data-testid="jobs-reset"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </button>
              )}
            </div>

            <AnimatePresence mode="popLayout">
              {filtered.length > 0 ? (
                <motion.div layout className="grid gap-6 sm:grid-cols-2">
                  {filtered.map((job, i) => (
                    <motion.div
                      key={job.id}
                      layout
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.4, ease: EASE, delay: (i % 4) * 0.05 }}
                    >
                      <JobCard job={job} onView={setSelectedJob} />
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="relative flex flex-col items-center justify-center overflow-hidden rounded-[28px] border border-white/[0.08] bg-white/[0.02] px-8 py-20 text-center"
                  data-testid="jobs-empty"
                >
                  <PrismHero className="w-28 opacity-70" />
                  <span className="mt-6 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-axor-cyan">
                    <SearchX className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 text-2xl font-semibold tracking-tight text-axor-white">
                    No opportunities match your search
                  </h3>
                  <p className="mt-2 max-w-sm text-sm text-axor-text">
                    Try adjusting your filters, or reach out and we'll help you
                    find the right fit.
                  </p>
                  <AxorButton onClick={reset} variant="secondary" icon={RotateCcw} className="mt-7" data-testid="jobs-empty-reset">
                    Clear Filters
                  </AxorButton>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Container>
      </section>

      <CTASection
        overline="Can't Find The Right Role?"
        title="Let's Find It Together."
        description="Tell us what you're looking for and we'll help you explore the options."
        showJobs={false}
      />

      <JobDetail job={selectedJob} onClose={() => setSelectedJob(null)} />
    </>
  );
}
